// ════════════════════════════════════════════════════════════════════════════
// Swapping located blocks for new text, in place.
//
// The one document-editing primitive the materialiser needs. Kept apart from
// everything that decides WHAT to write so that the writing itself — the part
// that must not disturb a byte outside the spans it was given — is one short
// function with its own tests.
// ════════════════════════════════════════════════════════════════════════════

import type { FenceSpan } from './fence-spans.js';

const NEWLINE = '\n';

export interface BlockReplacement {
  readonly span: FenceSpan;
  readonly text: string;
}

/**
 * `content` with each replacement's span swapped for its text.
 *
 * Replacements are taken in document order and applied left to right against
 * the ORIGINAL offsets, so no replacement's length shifts another's span.
 *
 * A block's span ends after its closing fence line, INCLUDING that line's
 * newline where it had one. The newline is put back after the replacement,
 * because it separates the block from whatever follows it and belongs to the
 * document rather than to the block: dropping it would weld the next paragraph
 * onto the sentinel's closing fence.
 */
export function spliceBlocks(input: {
  content: string;
  replacements: readonly BlockReplacement[];
}): string {
  const ordered = [...input.replacements].sort(
    (left, right) => left.span.start - right.span.start,
  );
  const { pieces, cursor } = ordered.reduce<{ pieces: string[]; cursor: number }>(
    (state, replacement) => {
      const { span } = replacement;
      if (span.start < state.cursor) return state;
      const trailedNewline = input.content[span.end - 1] === NEWLINE;
      return {
        pieces: [
          ...state.pieces,
          input.content.slice(state.cursor, span.start),
          replacement.text,
          trailedNewline ? NEWLINE : '',
        ],
        cursor: span.end,
      };
    },
    { pieces: [], cursor: 0 },
  );
  return [...pieces, input.content.slice(cursor)].join('');
}
